import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  ButtonGroup,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stack,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { ExpandLess, ExpandMore, Tune } from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { MapContainer } from '@components/map/MapContainer'
import { useLocationHeatmap } from '../hooks/useLocationHeatmap'
import { HeatmapTier } from '@api/endpoints/marketing'
import { HeatmapLayer } from '../components/HeatmapLayer'
import { GridCellLayer } from '../components/GridCellLayer'
import { INTENSITY_SCALE } from '../components/intensity'
import { CenterOnUser } from '../components/CenterOnUser'
import { MapViewControls } from '../components/MapViewControls'
import { useGeolocation } from '@hooks/useGeolocation'
import { PlaybackControls } from '../components/PlaybackControls'

type ViewMode = 'grid' | 'heat'

function hoursAgo(hours: number): Date {
  return new Date(Date.now() - hours * 60 * 60 * 1000)
}

export default function LocationActivityPage() {
  const [from, setFrom] = useState<Date | null>(hoursAgo(24))
  const [to, setTo] = useState<Date | null>(new Date())
  const [tier, setTier] = useState<HeatmapTier>('all')
  const [level, setLevel] = useState<5 | 6>(6)
  const [aggregate, setAggregate] = useState(false)
  const [view, setView] = useState<ViewMode>('grid')
  const [frameIndex, setFrameIndex] = useState(0)
  const [rangeLabel, setRangeLabel] = useState('Last 24h')

  // On a phone the map comes first and the long control stack folds behind a "Controls" button;
  // otherwise the controls alone fill the first screen and the map starts below the fold.
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const [controlsOpen, setControlsOpen] = useState(false)

  // Where the operator is, used once to place the map. The hook falls back to the account's home
  // location and then to a default, so this is rarely null even with the permission refused.
  const { location: userLocation } = useGeolocation()

  const params = useMemo(
    () => ({
      from: from ? from.toISOString() : '',
      to: to ? to.toISOString() : '',
      tier,
      level,
      aggregate,
    }),
    [from, to, tier, level, aggregate],
  )

  const { data, isLoading, isError, error, isFetching } = useLocationHeatmap(params)

  const frames = useMemo(() => data?.frames ?? [], [data])
  const maxWeight = data?.maxWeight ?? 1
  const allPoints = useMemo(() => frames.flatMap((f) => f.points), [frames])
  const currentPoints = frames[frameIndex]?.points ?? []

  // Reset playback to the first frame whenever a new dataset arrives.
  useEffect(() => {
    setFrameIndex(0)
  }, [data])

  const applyQuickRange = (hours: number, label: string) => {
    setFrom(hoursAgo(hours))
    setTo(new Date())
    setRangeLabel(label)
  }

  const controlsSummary = [
    rangeLabel,
    tier === 'all' ? 'All tiers' : tier[0].toUpperCase() + tier.slice(1),
    level === 6 ? 'Fine' : 'Coarse',
    view === 'grid' ? 'Grid' : 'Heatmap',
    aggregate ? 'Aggregate' : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const totalActivity = useMemo(
    () => allPoints.reduce((sum, p) => sum + p.weight, 0),
    [allPoints],
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ order: 0 }}>
        <Typography variant="body2" color="text.secondary">
          Anonymised heatmap of where users were active over a selected period. Values are
          per-window activity/visits — not distinct people — and cells with fewer than{' '}
          {data?.minCount ?? 3} visitors are hidden to protect individuals.
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, order: { xs: 2, sm: 1 } }}>
        {isXs && (
          <Button
            fullWidth
            onClick={() => setControlsOpen((o) => !o)}
            startIcon={<Tune />}
            endIcon={controlsOpen ? <ExpandLess /> : <ExpandMore />}
            aria-expanded={controlsOpen}
            sx={{ justifyContent: 'space-between', textAlign: 'left', minHeight: 40, mb: controlsOpen ? 1.5 : 0 }}
          >
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              Controls
              <Typography component="span" variant="caption" color="text.secondary" display="block" noWrap>
                {controlsSummary}
              </Typography>
            </Box>
          </Button>
        )}
        <Collapse in={!isXs || controlsOpen}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap">
              <DateTimePicker
                label="From"
                value={from}
                onChange={(v) => {
                  setFrom(v)
                  setRangeLabel('Custom range')
                }}
                disableFuture
                slotProps={{ textField: { size: 'small', fullWidth: isXs } }}
              />
              <DateTimePicker
                label="To"
                value={to}
                onChange={(v) => {
                  setTo(v)
                  setRangeLabel('Custom range')
                }}
                disableFuture
                minDateTime={from ?? undefined}
                slotProps={{ textField: { size: 'small', fullWidth: isXs } }}
              />

              <ButtonGroup size="small" variant="outlined">
                <Button onClick={() => applyQuickRange(24, 'Last 24h')}>24h</Button>
                <Button onClick={() => applyQuickRange(24 * 7, 'Last 7d')}>7d</Button>
                <Button onClick={() => applyQuickRange(24 * 30, 'Last 30d')}>30d</Button>
              </ButtonGroup>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={tier}
                onChange={(_, v) => v && setTier(v)}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="free">Free</ToggleButton>
                <ToggleButton value="basic">Basic</ToggleButton>
                <ToggleButton value="premium">Premium</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={level}
                onChange={(_, v) => v && setLevel(v)}
              >
                <ToggleButton value={6}>Fine (~1.2km)</ToggleButton>
                <ToggleButton value={5}>Coarse (~4.8km)</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={view}
                onChange={(_, v) => v && setView(v)}
              >
                <ToggleButton value="grid">Grid</ToggleButton>
                <ToggleButton value="heat">Heatmap</ToggleButton>
              </ToggleButtonGroup>

              <FormControlLabel
                control={<Switch checked={aggregate} onChange={(e) => setAggregate(e.target.checked)} />}
                label="Aggregate (whole period)"
              />
            </Stack>
          </LocalizationProvider>
        </Collapse>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ order: { xs: 0, sm: 2 } }}>
          {(error as Error)?.message || 'Failed to load location activity.'}
        </Alert>
      )}

      {/* Map + playback */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 1, order: { xs: 1, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {isLoading || isFetching ? (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} /> Loading…
              </Box>
            ) : (
              `${allPoints.length} cell readings · ${totalActivity.toLocaleString()} total visits`
            )}
          </Typography>
        </Box>

        {/* Sized off the viewport, not a fixed px floor: 500px of map on a 640px phone screen left
            no room for the playback bar under it. */}
        <Box sx={{ height: { xs: '55vh', md: '65vh' }, minHeight: { xs: 260, md: 400 }, position: 'relative' }}>
          {/* The map is always rendered now, even with nothing to plot. It used to be replaced by
              the "no activity" alert, which meant an empty result gave you a blank panel and no way
              to see where you were looking — the one situation where being shown your own area is
              most useful. The message is an overlay instead. */}
          <MapContainer height="100%" zoom={11}>
            <CenterOnUser location={userLocation} />
            <MapViewControls points={allPoints} location={userLocation} />
            {view === 'grid' ? (
              <GridCellLayer points={currentPoints} maxWeight={maxWeight} tier={tier} perWindow={!aggregate} />
            ) : (
              <HeatmapLayer points={currentPoints} maxWeight={maxWeight} />
            )}
          </MapContainer>

          {!isLoading && allPoints.length === 0 && (
            <Alert
              severity="info"
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                // Clear of the map's view buttons: labelled at sm+, a stacked icon column on a phone.
                right: { xs: 62, sm: 200 },
                zIndex: 1000,
                // Between sm and 700px the labelled buttons sit above the alert rather than beside it.
                '@media (min-width: 600px) and (max-width: 700px)': { right: 10, top: 56 },
              }}
            >
              No location activity found for this period. Try a wider range, a coarser
              granularity, or the "All" tier.
            </Alert>
          )}

          {/* Legend (grid view): color scale from few → many visits */}
          {view === 'grid' && allPoints.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 12,
                right: 12,
                zIndex: 1000,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1,
                py: 0.5,
                boxShadow: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {aggregate ? 'Visits' : 'Unique users'} / cell
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption">1</Typography>
                {INTENSITY_SCALE.map((c) => (
                  <Box key={c} sx={{ width: 16, height: 12, bgcolor: c, border: '1px solid #444' }} />
                ))}
                <Typography variant="caption">{maxWeight.toLocaleString()}</Typography>
              </Box>
            </Box>
          )}
        </Box>

        {frames.length > 0 && (
          <PlaybackControls
            frames={frames}
            frameIndex={frameIndex}
            onFrameIndexChange={setFrameIndex}
          />
        )}
      </Paper>
    </Box>
  )
}
